import React from "react";
import { FiBriefcase, FiPlus } from "react-icons/fi";
import { useFormModal } from "@core/hooks/useFormModal";
import { FormModal, FormModalField } from "@core/components/FormModal";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { createDepartmentAsync } from "@features/department/services/departmentSvc";
import { CreateDepartmentRequest } from "@features/department/types/departmentRequest";

const FIELDS: FormModalField<CreateDepartmentRequest>[] = [
  {
    name: "name",
    label: "Department Name",
    type: "text",
    required: true,
    placeholder: "Enter department name",
    helpText: "A clear name for the department, e.g. Women Ministries Department or Youth Department.",
  },
  {
    name: "description",
    label: "Description",
    type: "textarea",
    rows: 4,
    placeholder: "Enter department description",
    helpText: "Optional: a brief description of the department's purpose and activities.",
  },
];

const CreateDepartmentModal: React.FC<{
  show: boolean;
  onHide: () => void;
  nationId: number;
  onChanged?: () => void;
}> = ({ show, onHide, nationId, onChanged }) => {
  const scopeId = useScopeId();
  const resolvedNationId = nationId || scopeId || 0;

  const { form, formErrors, busy, handleChange, setFieldValue, handleSubmit } =
    useFormModal<CreateDepartmentRequest>({
      initial: { name: "", description: "" },
      validate: (f) => {
        const errors: Partial<Record<keyof CreateDepartmentRequest, string[]>> = {};
        const messages: string[] = [];
        const name = f.name?.trim() ?? "";
        const description = f.description?.trim() ?? "";

        if (!name) {
          errors.name = ["Department name is required."];
          messages.push("Department name is required.");
        } else if (name.length > 100) {
          errors.name = ["Department name must be at most 100 characters."];
          messages.push("Department name must be at most 100 characters.");
        }

        if (description.length > 255) {
          errors.description = ["Description must be at most 255 characters."];
          messages.push("Description must be at most 255 characters.");
        }

        return { errors, messages };
      },
      submit: (f) =>
        createDepartmentAsync(resolvedNationId, {
          name: f.name.trim(),
          description: f.description?.trim() || "",
        }),
      onSuccess: () => {
        onHide();
        onChanged?.();
      },
      errorFallback: "Failed to create department",
    });

  return (
    <FormModal<CreateDepartmentRequest>
      show={show}
      onHide={onHide}
      title="Create Department"
      icon={<FiBriefcase aria-hidden />}
      fields={FIELDS}
      form={form}
      formErrors={formErrors}
      busy={busy}
      onChange={handleChange}
      setFieldValue={setFieldValue}
      onSubmit={handleSubmit}
      submitLabel="Create Department"
      busyLabel="Creating…"
      submitIcon={<FiPlus className="me-2" />}
      testId="create-department-modal"
    />
  );
};

export default CreateDepartmentModal;
